class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification =
      this.querySelector('#cart-notification') ||
      document.getElementById('cart-notification');

    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.activeElement = null;
    this.isRefreshing = false;

    this.notification?.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') {
        this.close();
      }
    });

    this.querySelectorAll('button[type="button"]').forEach((closeButton) => {
      if (
        closeButton.classList.contains('cart-notification__close') ||
        closeButton.closest('.cart-notification__links')
      ) {
        closeButton.addEventListener('click', this.close.bind(this));
      }
    });
  }

  open() {
    if (!this.notification) return;

    this.notification.classList.add('animate', 'active');
    document.body.classList.add('cs-cart-notification-open');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();

        if (typeof trapFocus === 'function') {
          trapFocus(this.notification);
        }
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (!this.notification) return;

    this.notification.classList.remove('active');
    document.body.classList.remove('cs-cart-notification-open');
    document.body.removeEventListener('click', this.onBodyClick);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  handleBodyClick(event) {
    const target = event.target;

    if (
      target !== this.notification &&
      !target.closest('cart-notification') &&
      !target.closest('product-form') &&
      !target.closest('quick-add-modal')
    ) {
      this.close();
    }
  }

  async renderContents(
    parsedState,
    {
      open = true
    } = {}
  ) {
    this.updateCartBubbleFromSections(
      parsedState
    );

    try {
      await this.refreshFromCart(
        null,
        {
          open
        }
      );
    } catch (error) {
      console.error(
        'Cold Sugar cart notification refresh failed:',
        error
      );

      if (open) {
        this.renderNotificationError(
          'The item was added, but the cart preview could not be refreshed. Open your cart to review it.'
        );

        this.open();
      }
    }
  }

  async refreshFromCart(
    cart = null,
    {
      open = true
    } = {}
  ) {
    if (this.isRefreshing) return;

    this.isRefreshing = true;

    try {
      const cartState = cart || (await this.fetchCart());
      const visibleItems = this.getVisibleItems(cartState);

      if (visibleItems.length === 0) {
        this.close();
        return;
      }

      this.renderFullCart(
        cartState
      );

      if (open) {
        this.open();
      } else {
        this.close();
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  renderFullCart(cart) {
    const container = this.querySelector('#cart-notification-product');
    if (!container) return;

    const visibleItems = this.getVisibleItems(cart);
    container.replaceChildren();

    const list = document.createElement('div');
    list.className = 'cs-cart-notification__items';
    list.setAttribute('role', 'list');

    visibleItems.forEach((item) => {
      list.appendChild(this.createCartRow(item, cart));
    });

    const summary = document.createElement('div');
    summary.className = 'cs-cart-notification__summary';

    const itemCount = visibleItems.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0
    );

    const countLabel = document.createElement('span');
    countLabel.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;

    const subtotal = document.createElement('span');
    const subtotalLabel = document.createElement('strong');
    subtotalLabel.textContent = 'Subtotal';
    subtotal.append(
      subtotalLabel,
      document.createTextNode(
        ` ${this.formatMoney(
          cart.items_subtotal_price ?? cart.total_price ?? 0,
          cart.currency
        )}`
      )
    );

    summary.append(countLabel, subtotal);
    container.append(list, summary);

    const shippingBoxSlot =
      this.querySelector(
        '[data-cs-shipping-box-notification-slot]'
      );

    if (shippingBoxSlot) {
      shippingBoxSlot.replaceChildren();

      const shippingBoxStatus =
        window.ColdSugarShippingBoxUI
          ?.createNotificationStatus?.(cart);

      if (shippingBoxStatus) {
        shippingBoxSlot.appendChild(
          shippingBoxStatus
        );
        shippingBoxSlot.hidden = false;
      } else {
        shippingBoxSlot.hidden = true;
      }
    }
  }

  createCartRow(item, cart) {
    const row = document.createElement('article');
    row.className = 'cs-cart-notification__item';
    row.setAttribute('role', 'listitem');
    row.dataset.cartItemKey = item.key;

    const imageLink = document.createElement('a');
    imageLink.className = 'cs-cart-notification__image-link';
    imageLink.href = item.url || '#';
    imageLink.setAttribute(
      'aria-label',
      `View ${item.product_title || item.title || 'product'}`
    );

    if (item.image) {
      const image = document.createElement('img');
      image.className = 'cs-cart-notification__image';
      image.src = item.image;
      image.alt =
        item.featured_image?.alt ||
        item.product_title ||
        item.title ||
        '';
      image.loading = 'lazy';
      image.width = 96;
      image.height = 96;
      imageLink.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'cs-cart-notification__image-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      imageLink.appendChild(placeholder);
    }

    const details = document.createElement('div');
    details.className = 'cs-cart-notification__details';

    const title = document.createElement('a');
    title.className = 'cs-cart-notification__title';
    title.href = item.url || '#';
    title.textContent = item.product_title || item.title || 'Product';
    details.appendChild(title);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.className = 'cs-cart-notification__variant';
      variant.textContent = item.variant_title;
      details.appendChild(variant);
    }

    const quantityRule = item.quantity_rule || {};
    const minimum = Math.max(1, Number(quantityRule.min) || 1);
    const increment = Math.max(1, Number(quantityRule.increment) || 1);
    const maximum = quantityRule.max ? Number(quantityRule.max) : null;

    const controls = document.createElement('div');
    controls.className = 'cs-cart-notification__line-controls';

    const quantity = document.createElement('div');
    quantity.className = 'cs-cart-notification__quantity';

    const minus = document.createElement('button');
    minus.className = 'cs-cart-notification__quantity-button';
    minus.type = 'button';
    minus.textContent = '−';
    minus.disabled = Number(item.quantity) <= minimum;
    minus.setAttribute(
      'aria-label',
      `Decrease quantity for ${item.product_title || item.title || 'product'}`
    );

    const input = document.createElement('input');
    input.className = 'cs-cart-notification__quantity-input';
    input.type = 'number';
    input.value = String(item.quantity);
    input.min = String(minimum);
    input.step = String(increment);
    input.inputMode = 'numeric';
    input.setAttribute(
      'aria-label',
      `Quantity for ${item.product_title || item.title || 'product'}`
    );

    if (maximum) {
      input.max = String(maximum);
    }

    const plus = document.createElement('button');
    plus.className = 'cs-cart-notification__quantity-button';
    plus.type = 'button';
    plus.textContent = '+';
    plus.disabled = maximum ? Number(item.quantity) >= maximum : false;
    plus.setAttribute(
      'aria-label',
      `Increase quantity for ${item.product_title || item.title || 'product'}`
    );

    quantity.append(minus, input, plus);

    const linePrice = document.createElement('span');
    linePrice.className = 'cs-cart-notification__line-price';
    linePrice.textContent = this.formatMoney(
      item.final_line_price ?? item.line_price ?? 0,
      cart.currency
    );

    controls.append(quantity, linePrice);

    const remove = document.createElement('button');
    remove.className = 'cs-cart-notification__remove';
    remove.type = 'button';
    remove.textContent = 'Remove';
    remove.setAttribute(
      'aria-label',
      `Remove ${item.product_title || item.title || 'product'} from cart`
    );

    const updateQuantity = async (requestedQuantity) => {
      const normalized = this.normalizeQuantity(
        requestedQuantity,
        minimum,
        maximum,
        increment
      );

      input.value = String(normalized);

      if (normalized === Number(item.quantity)) return;

      try {
        const shippingValidation =
          await this.validateShippingBoxQuantityChange(
            item.key,
            normalized,
            Number(item.quantity)
          );

        if (!shippingValidation.allowed) {
          input.value = String(item.quantity);

          this.showLineError(
            row,
            shippingValidation.message
          );

          return;
        }
      } catch (error) {
        input.value = String(item.quantity);

        this.showLineError(
          row,
          error.message ||
            'Your shipping box could not be checked. Please try again.'
        );

        return;
      }

      await this.changeLine(item.key, normalized, row);
    };

    minus.addEventListener('click', () => {
      void updateQuantity(Number(input.value) - increment);
    });

    plus.addEventListener('click', () => {
      void updateQuantity(Number(input.value) + increment);
    });

    input.addEventListener('change', () => {
      void updateQuantity(Number(input.value));
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        input.blur();
      }
    });

    remove.addEventListener('click', () => {
      void this.changeLine(item.key, 0, row);
    });

    details.append(controls, remove);
    row.append(imageLink, details);

    return row;
  }

  normalizeQuantity(value, minimum, maximum, increment) {
    let quantity = Number(value);

    if (!Number.isFinite(quantity)) {
      quantity = minimum;
    }

    quantity = Math.max(minimum, quantity);

    const steps = Math.round((quantity - minimum) / increment);
    quantity = minimum + steps * increment;

    if (maximum) {
      quantity = Math.min(maximum, quantity);
    }

    return quantity;
  }

  async validateShippingBoxQuantityChange(
    itemKey,
    requestedQuantity,
    currentQuantity
  ) {
    /*
      Removing or decreasing an item can never make the shipping
      box more over capacity, so allow those immediately.
    */
    if (
      requestedQuantity === 0 ||
      requestedQuantity <= currentQuantity
    ) {
      return {
        allowed: true,
        message: '',
      };
    }

    const response =
      await fetch(this.route('cart.js'), {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

    if (!response.ok) {
      throw new Error(
        'Your shipping box could not be checked. Please try again.'
      );
    }

    const cart =
      await response.json();

    const targetItem =
      (cart.items || []).find(
        (cartItem) =>
          String(cartItem.key) ===
          String(itemKey)
      );

    if (!targetItem) {
      throw new Error(
        'This cart item could not be checked. Please refresh the page and try again.'
      );
    }

    const orderMode =
      String(
        targetItem?.properties
          ?._cold_sugar_order_mode || ''
      )
        .trim()
        .toLowerCase();

    if (orderMode !== 'shipping') {
      return {
        allowed: true,
        message: '',
      };
    }

    const shippingBox =
      window.ColdSugarShippingBox;

    if (
      !shippingBox ||
      typeof shippingBox.previewLineQuantity !==
        'function'
    ) {
      throw new Error(
        'Shipping box protection is still loading. Please wait a moment and try again.'
      );
    }

    const result =
      shippingBox.previewLineQuantity(
        cart.items || [],
        targetItem.key,
        requestedQuantity
      );

    if (!result.valid) {
      return {
        allowed: false,
        message:
          result.message ||
          'That quantity will not fit in our available shipping boxes.',
      };
    }

    return {
      allowed: true,
      message: '',
      packingResult: result,
    };
  }

  async changeLine(itemKey, quantity, row) {
    this.setLineLoading(row, true);
    this.clearLineError(row);

    try {
      const response = await fetch(this.route('cart/change.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status || result.errors) {
        throw new Error(
          result.description ||
            result.message ||
            result.errors ||
            'We could not update this item.'
        );
      }

      const cart = await this.cleanupIfVisiblyEmpty(result);
      await this.refreshCartBubble();
      this.publishCartUpdate(cart);

      if (this.getVisibleItems(cart).length === 0) {
        this.close();
        return;
      }

      this.renderFullCart(cart);
    } catch (error) {
      this.setLineLoading(row, false);
      this.showLineError(
        row,
        error.message || 'We could not update this item. Please try again.'
      );
    }
  }

  async cleanupIfVisiblyEmpty(cart) {
    if (this.getVisibleItems(cart).length > 0) {
      return cart;
    }

    const updates = {};

    (cart.items || []).forEach((item) => {
      if (this.isPickupLock(item)) {
        updates[item.key] = 0;
      }
    });

    const attributes = {};
    this.getFulfillmentAttributeNames().forEach((name) => {
      attributes[name] = '';
    });

    const response = await fetch(this.route('cart/update.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        updates,
        attributes,
      }),
    });

    const updatedCart = await response.json();

    if (!response.ok || updatedCart.errors) {
      throw new Error(
        updatedCart.description ||
          updatedCart.message ||
          'The item was removed, but the cart could not be fully reset.'
      );
    }

    return updatedCart;
  }

  getVisibleItems(cart) {
    return (cart?.items || []).filter((item) => !this.isPickupLock(item));
  }

  isPickupLock(item) {
    const value = item?.properties?._cold_sugar_pickup_lock;

    return (
      value === true ||
      value === 'true' ||
      value === 1 ||
      value === '1' ||
      item?.handle === 'pickup-location'
    );
  }

  getFulfillmentAttributeNames() {
    return [
      'Fulfillment Category',
      'Fulfillment Type',
      'Fulfillment Option Handle',
      'Fulfillment Option Name',
      'Pickup Event ID',
      'Pickup Date',
      'Pickup Day',
      'Pickup Location',
      'Pickup City State',
      'Pickup Address',
      'Pickup Window',
      'Pickup Instructions',
      'Delivery Event ID',
      'Delivery Date',
      'Delivery Day',
      'Delivery Area',
      'Delivery Window',
      'Delivery Zip Code',
      'Delivery Instructions',
      'Shipping Event ID',
      'Shipping Option',
      'Requested Delivery Date',
      'Requested Delivery Day',
      'Estimated Ship Date',
      'Shipping Method',
      'Checkout Method To Select',
      'Shopping Path',
      'Porch Live Purchase',
      'Thursday Preorder Close Time',
      'Thursday Live Open Time',
      'Thursday Live Close Time',
    ];
  }

  setLineLoading(row, isLoading) {
    if (!row) return;

    row.classList.toggle('is-updating', isLoading);
    row.setAttribute('aria-busy', String(isLoading));

    row.querySelectorAll('button, input').forEach((control) => {
      control.disabled = isLoading;
    });
  }

  showLineError(row, message) {
    if (!row) return;

    let error = row.querySelector('[data-notification-line-error]');

    if (!error) {
      error = document.createElement('p');
      error.className = 'cs-cart-notification__error';
      error.dataset.notificationLineError = '';
      error.setAttribute('role', 'alert');
      row.querySelector('.cs-cart-notification__details')?.appendChild(error);
    }

    error.textContent = message;
  }

  clearLineError(row) {
    row?.querySelector('[data-notification-line-error]')?.remove();
  }

  renderNotificationError(message) {
    const container = this.querySelector('#cart-notification-product');
    if (!container) return;

    container.innerHTML = '';

    const error = document.createElement('p');
    error.className = 'cs-cart-notification__standalone-error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    container.appendChild(error);
  }

  publishCartUpdate(cart) {
    if (
      typeof publish === 'function' &&
      window.PUB_SUB_EVENTS &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-notification',
        cartData: cart,
      });
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart },
      })
    );
  }

  updateCartBubbleFromSections(parsedState) {
    const sectionHTML = parsedState?.sections?.['cart-icon-bubble'];
    const target = document.getElementById('cart-icon-bubble');

    if (!sectionHTML || !target) return;

    const html = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const source =
      html.querySelector('.shopify-section') ||
      html.querySelector('#cart-icon-bubble');

    if (source) {
      target.innerHTML = source.innerHTML;
    }
  }

  async refreshCartBubble() {
    const target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    try {
      const response = await fetch(
        `${this.route('cart')}?section_id=cart-icon-bubble`,
        {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      if (!response.ok) return;

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const source =
        html.querySelector('.shopify-section') ||
        html.querySelector('#cart-icon-bubble');

      if (source) {
        target.innerHTML = source.innerHTML;
      }
    } catch (error) {
      console.error('Cold Sugar cart bubble refresh failed:', error);
    }
  }

  async fetchCart() {
    const response = await fetch(this.route('cart.js'), {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error('We could not read your cart.');
    }

    return response.json();
  }

  formatMoney(cents, currency = 'USD') {
    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
        {
          style: 'currency',
          currency,
        }
      ).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  route(path) {
    const root =
      (window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root) ||
      '/';

    return `${root.replace(/\/?$/, '/')}${String(path).replace(/^\//, '')}`;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: '#cart-notification-product',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}